import type { ReactNode } from 'react'
import { cn } from '@/utils/cn'
import { GestorDashboardTaskLink } from '@/components/Gestores/GestorDashboardTaskIdChip'
import { formatEffortMinutes, formatCurrency, formatNumber, formatPercent } from '@/components/Gestores/gestorDashboardFormat'

export type GestorDashboardBreakdownListRow = {
  key: string
  label: string
  effortMinutes?: number
  count?: number
  taskId?: number | null
}

export function GestorDashboardMetricCard({
  label,
  value,
  tone = 'neutral',
  icon,
  compact = false,
}: {
  label: string
  value: string
  tone?: 'neutral' | 'success' | 'danger'
  icon?: ReactNode
  compact?: boolean
}) {
  return (
    <div
      className={cn(
        'glass-card flex min-h-[6.5rem] flex-col justify-between transition-colors',
        compact ? 'p-3' : 'p-4',
        tone === 'success' && 'border-l-2 border-l-emerald-500/70',
        tone === 'danger' && 'border-l-2 border-l-red-500/70',
      )}
    >
      <div className='flex items-start justify-between gap-2'>
        <p className='min-w-0 text-[10px] font-semibold uppercase leading-snug tracking-wide text-text-muted sm:text-xs'>
          {label}
        </p>
        {icon ? (
          <div className='shrink-0 rounded-lg bg-accent/10 p-1.5 text-accent' aria-hidden>
            {icon}
          </div>
        ) : null}
      </div>
      <p
        className={cn(
          'mt-2 font-bold tabular-nums text-text-primary',
          compact ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl',
        )}
      >
        {value}
      </p>
    </div>
  )
}

function PanelShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className='glass-card p-4 sm:p-5'>
      <h3 className='mb-4 text-sm font-semibold text-text-primary'>{title}</h3>
      {children}
    </div>
  )
}

export function GestorDashboardBreakdownList({
  title,
  rows,
  valueFormatter = formatEffortMinutes,
  valueKey = 'effortMinutes' as const,
  onTaskOpen,
  onRowClick,
  activeRowKey,
}: {
  title: string
  rows: GestorDashboardBreakdownListRow[]
  valueFormatter?: (value: number) => string
  valueKey?: 'effortMinutes' | 'count'
  onTaskOpen?: (taskId: number) => void
  onRowClick?: (row: GestorDashboardBreakdownListRow) => void
  activeRowKey?: string | null
}) {
  const max = Math.max(...rows.map((row) => row[valueKey] ?? 0), 1)

  return (
    <PanelShell title={title}>
      {rows.length === 0 ? (
        <p className='text-sm text-text-muted'>Sem dados no período.</p>
      ) : (
        <ul className='space-y-3'>
          {rows.slice(0, 10).map((row) => {
            const value = row[valueKey] ?? 0
            const isActive = activeRowKey != null && activeRowKey === row.key
            return (
              <li key={row.key}>
                <div className='mb-1.5 flex items-center justify-between gap-3 text-xs sm:text-sm'>
                  {row.taskId && onTaskOpen ? (
                    <GestorDashboardTaskLink
                      taskId={row.taskId}
                      label={row.label}
                      onOpen={onTaskOpen}
                      className='min-w-0 flex-1'
                    />
                  ) : onRowClick ? (
                    <button
                      type='button'
                      onClick={() => onRowClick(row)}
                      className={cn(
                        'min-w-0 truncate text-left font-medium transition-colors hover:text-accent',
                        isActive ? 'text-accent' : 'text-text-primary',
                      )}
                      title={`Ver tarefas de ${row.label}`}
                    >
                      {row.label}
                    </button>
                  ) : (
                    <span className='min-w-0 truncate font-medium text-text-primary'>{row.label}</span>
                  )}
                  <span className='shrink-0 tabular-nums text-text-muted'>{valueFormatter(value)}</span>
                </div>
                <div className='h-2 rounded-full bg-[var(--glass-border)]'>
                  <div
                    className={cn('h-full rounded-full', isActive ? 'bg-accent' : 'bg-accent/80')}
                    style={{ width: `${Math.max(8, (value / max) * 100)}%` }}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </PanelShell>
  )
}

export function GestorDashboardMonthlyBars({
  title,
  rows,
  valueKey,
}: {
  title: string
  rows: Array<{ month: string; label: string; count: number; effortMinutes: number }>
  valueKey: 'count' | 'effortMinutes'
}) {
  const max = Math.max(...rows.map((row) => row[valueKey]), 1)
  const formatValue = valueKey === 'effortMinutes' ? formatEffortMinutes : formatNumber

  return (
    <PanelShell title={title}>
      {rows.length === 0 ? (
        <p className='text-sm text-text-muted'>Sem histórico no período.</p>
      ) : (
        <div className='flex items-end gap-3 overflow-x-auto pb-1'>
          {rows.map((row) => (
            <div key={row.month} className='flex min-w-[52px] flex-1 flex-col items-center gap-2'>
              <div className='flex h-32 w-full max-w-[3rem] items-end rounded-lg bg-[var(--glass-border)] px-1'>
                <div
                  className='w-full rounded-md bg-accent/80'
                  style={{ height: `${Math.max(8, (row[valueKey] / max) * 100)}%` }}
                />
              </div>
              <span className='text-[10px] font-medium text-text-muted'>{row.label}</span>
              <span className='text-[11px] font-semibold tabular-nums text-text-primary'>
                {formatValue(row[valueKey])}
              </span>
            </div>
          ))}
        </div>
      )}
    </PanelShell>
  )
}

export { formatCurrency, formatNumber, formatPercent }
